/**
 * Preparing uploaded HTML for the sandboxed viewer frame.
 *
 * The frame runs without `allow-same-origin`, so the file can never reach the parent's login or
 * the rest of the site. That also makes `localStorage`/`sessionStorage` throw, which breaks many
 * interactive pages, so a small script is added first that swaps in in-memory storage when needed.
 * The same script wraps `history.replaceState`/`pushState` (null-origin srcdoc throws SecurityError,
 * which otherwise stops hub week-tab handlers that call replaceState before showTab), and blocks
 * save/print shortcuts, right-click and download links inside the frame (key presses inside an
 * iframe never reach the page around it).
 *
 * Watermarks are injected inside the HTML (not as a parent overlay). An overlay above an iframe
 * often swallows clicks even with pointer-events: none, which made week tabs / buttons feel dead.
 */

/** Sandbox flags: scripts, forms, alerts and outside links work; downloads and site access don't. */
export const HTML_SANDBOX = "allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox";

const GUARD_SCRIPT = `<script data-ryd-viewer>(function () {
  function memoryStorage() {
    var data = {};
    return {
      get length() { return Object.keys(data).length; },
      key: function (i) { return Object.keys(data)[i] || null; },
      getItem: function (k) { k = String(k); return Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null; },
      setItem: function (k, v) { data[String(k)] = String(v); },
      removeItem: function (k) { delete data[String(k)]; },
      clear: function () { data = {}; }
    };
  }
  ["localStorage", "sessionStorage"].forEach(function (name) {
    try { window[name].getItem("__ryd"); } catch (e) {
      try { Object.defineProperty(window, name, { value: memoryStorage(), configurable: true }); } catch (e2) {}
    }
  });
  // Null-origin sandboxes (srcdoc without allow-same-origin) throw on history.replaceState/pushState.
  // Hub HTML often calls replaceState before showTab — swallow so week tabs still switch.
  try {
    var proto = History.prototype;
    ["replaceState", "pushState"].forEach(function (m) {
      var orig = proto[m];
      if (typeof orig !== "function") return;
      proto[m] = function () {
        try { return orig.apply(this, arguments); } catch (err) { return null; }
      };
    });
  } catch (e) {}
  document.addEventListener("keydown", function (e) {
    var k = (e.key || "").toLowerCase();
    if ((e.ctrlKey || e.metaKey) && (k === "s" || k === "p")) { e.preventDefault(); e.stopPropagation(); }
  }, true);
  document.addEventListener("contextmenu", function (e) { e.preventDefault(); }, true);
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest("a[download]") : null;
    if (a) { e.preventDefault(); e.stopPropagation(); }
  }, true);
  try { window.print = function () {}; } catch (e) {}
})();</script>`;

function escapeHtml(value: string) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function watermarkMarkup(label: string) {
    const text = escapeHtml(label || "RYD Learning");
    const tiles = Array.from({ length: 40 }, () => `<span>${text}</span>`).join("");
    return `<style data-ryd-watermark>
html, body { min-height: 100%; }
[data-ryd-watermark-layer] {
  position: fixed; inset: 0; z-index: 2147483646; pointer-events: none !important;
  overflow: hidden; opacity: 0.07; user-select: none;
}
[data-ryd-watermark-layer] > div {
  position: absolute; inset: -50%; display: flex; flex-wrap: wrap; align-content: flex-start;
  gap: 5rem 4rem; padding: 2.5rem; transform: rotate(-24deg);
  font: 600 14px/1.2 system-ui, sans-serif; color: #111;
}
[data-ryd-watermark-layer] span { white-space: nowrap; }
</style><div data-ryd-watermark-layer aria-hidden="true"><div>${tiles}</div></div>`;
}

/** Adds the guard script (and optional in-document watermark) without breaking the doctype. */
export function prepareHtmlForViewer(html: string, options?: { watermark?: string }): string {
    const source = String(html ?? "");
    const extras = GUARD_SCRIPT + (options?.watermark ? watermarkMarkup(options.watermark) : "");
    const insertAfter = (match: RegExpMatchArray | null) =>
        match && match.index !== undefined
            ? source.slice(0, match.index + match[0].length) + extras + source.slice(match.index + match[0].length)
            : null;
    return (
        insertAfter(source.match(/<head(\s[^>]*)?>/i)) ??
        insertAfter(source.match(/<html(\s[^>]*)?>/i)) ??
        insertAfter(source.match(/^\s*<!doctype[^>]*>/i)) ??
        extras + source
    );
}
